import { useEffect, useRef, type ReactNode, type RefObject } from 'react';

import { MARKETS } from '../config.ts';

const GLYPH: Record<string, string> = { HYPE: 'H' };

/** Hyperliquid's blob mark (official SVG kit), mint on its dark green: the HYPE token's icon */
const HYPE_BLOB =
  'M175.11589,99.27891c0,49.46366-30.3687,65.33789-46.47302,51.30413-13.11357-11.5031-17.02471-35.88989-36.81018-38.42055-25.07698-2.99085-27.37764,30.36839-43.94216,30.36839-19.3254,0-23.00642-27.83773-23.00642-42.33179,0-14.72405,4.14115-34.73961,20.47565-34.73961,19.09528,0,20.24566,28.75802,44.17226,27.14754,23.69661-1.61047,24.1567-31.51877,39.80103-44.17224,13.57335-11.27313,45.78283.69019,45.78283,50.84413Z';

const ICONS: Record<string, (size: number) => ReactNode> = {
  HYPE: (size) => (
    <svg width={size} height={size} viewBox="0 0 200 200" className="coin-svg" aria-hidden>
      <circle cx="100" cy="100" r="100" fill="#011916" />
      <path d={HYPE_BLOB} fill="#97fce4" transform="translate(100 100) scale(0.96) translate(-100 -104)" />
    </svg>
  ),
};

export function CoinIcon({ market, size = 22 }: { market: number; size?: number }) {
  const m = MARKETS[market];
  const icon = ICONS[m?.symbol ?? ''];
  if (icon) return icon(size);
  return (
    <span className="coin" style={{ width: size, height: size, fontSize: size * 0.6, background: m?.color ?? '#999' }} aria-hidden>
      {GLYPH[m?.symbol ?? ''] ?? m?.symbol?.[0] ?? '?'}
    </span>
  );
}

export function Card({ title, right, children, className = '' }: { title?: ReactNode; right?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card ${className}`}>
      {(title || right) && (
        <header className="card-head">
          <h3>{title}</h3>
          {right}
        </header>
      )}
      {children}
    </section>
  );
}

export function Row({ label, children, tone }: { label: ReactNode; children: ReactNode; tone?: 'pos' | 'neg' | '' }) {
  return (
    <div className="row">
      <span className="row-label">{label}</span>
      <span className={`row-value num ${tone ?? ''}`}>{children}</span>
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  right,
}: {
  tabs: { id: T; label: string; count?: number }[];
  value: T;
  onChange: (t: T) => void;
  right?: ReactNode;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.id} role="tab" aria-selected={value === t.id} className={value === t.id ? 'is-on' : ''} onClick={() => onChange(t.id)}>
          {t.label}
          {t.count ? <span className="badge">{t.count}</span> : null}
        </button>
      ))}
      <span className="grow" />
      {right}
    </div>
  );
}

export const tone = (v: bigint | number | undefined): 'pos' | 'neg' | '' =>
  v === undefined ? '' : v > 0 ? 'pos' : v < 0 ? 'neg' : '';

/** Compact USD: $8.45K, $161M, from an 18-decimal amount */
export function compactUsd(wad: bigint): string {
  const v = Number(wad / 10n ** 12n) / 1e6;
  const sign = v < 0 ? '-' : '';
  const a = Math.abs(v);
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(a >= 1e8 ? 0 : 1)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(a >= 1e5 ? 0 : 2)}K`;
  return `${sign}$${a.toFixed(2)}`;
}

export function compactNum(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  if (a >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

export function ago(sec: number): string {
  const d = Math.max(0, Math.floor(Date.now() / 1000 - sec));
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86_400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86_400)}d ago`;
}

/** Copies `text`; false where the page has no clipboard (an insecure origin, an old browser) */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Focus management of a modal dialog: the first control gets the focus when it opens, Tab stays inside it,
 * and whatever had the focus gets it back when it closes. Attach the ref to the dialog element.
 */
export function useDialogFocus(): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const opener = document.activeElement as HTMLElement | null;
    const controls = () => [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((c) => c.offsetParent !== null);
    controls()[0]?.focus();
    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const cs = controls();
      if (cs.length === 0) return;
      const first = cs[0]!;
      const last = cs[cs.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    el.addEventListener('keydown', trap);
    return () => {
      el.removeEventListener('keydown', trap);
      opener?.focus?.();
    };
  }, []);
  return ref;
}

/** Calls `onClose` on Escape while mounted */
export function useEscape(onClose: () => void): void {
  useEffect(() => {
    const on = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [onClose]);
}
